import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useFeatureGroupUpsertMutation } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.upsert.mutation"
import { evictFeatureGroupQueries } from "@/v2-backoffice-client/license/license.utils"
import type { FeatureGroupFragment } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.fragment"

export type LicenseFeatureGroupUpsertDialogProps = {
  /** Null/undefined = create a new group. */
  featureGroup?: FeatureGroupFragment | null
  open: boolean
  onOpenChange(open: boolean): void
}

// Creates or renames a package — the identity fields only. Which features
// the package bundles is the package panel's Features tab's job, so neither
// write can half-apply the other. Codes are the stable identity licenses and
// add-ons hang packaging on; the API rejects collisions with a readable
// error.
function LicenseFeatureGroupUpsertDialog(
  props: LicenseFeatureGroupUpsertDialogProps,
) {
  const { featureGroup, open, onOpenChange } = props
  const isCreate = !featureGroup

  const [code, setCode] = useState(featureGroup?.Code ?? "")
  const [name, setName] = useState(featureGroup?.Name ?? "")
  const [description, setDescription] = useState(
    featureGroup?.Description ?? "",
  )

  const [upsertGroup, { loading, error }] = useFeatureGroupUpsertMutation({
    update: evictFeatureGroupQueries,
    onCompleted(data) {
      toast({
        variant: "success",
        title: isCreate ? "Package created" : "Package saved",
        description: isCreate
          ? `${data.upsertFeatureGroup.Name} is ready — add features to it next.`
          : `${data.upsertFeatureGroup.Name} was updated.`,
      })
      if (isCreate) {
        setCode("")
        setName("")
        setDescription("")
      }
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {isCreate ? "New Package" : `Edit Package — ${featureGroup.Name}`}
          </Dialog.Title>
          <Dialog.Description>
            A named bundle of features, included in any license and add-on.
            Renaming is safe — licenses and add-ons reference the package, not
            its name.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Code</Label>
            <Input
              value={code}
              placeholder="e.g. MARKET_DATA"
              onChange={(e) => {
                setCode(e.target.value)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Stable identifier, unique across packages.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              placeholder="e.g. Market data"
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              placeholder="What this package means commercially (optional)"
              onChange={(e) => {
                setDescription(e.target.value)
              }}
            />
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            disabled={!code.trim() || !name.trim()}
            data-feature-code="UI_BackofficeFeatureGroup_Upsert_Save"
            onClick={() => {
              upsertGroup({
                variables: {
                  input: {
                    Id: featureGroup?.Id ?? null,
                    Code: code.trim(),
                    Name: name.trim(),
                    Description: description.trim() || null,
                  },
                },
              })
            }}
          >
            {loading
              ? "Saving..."
              : isCreate
                ? "Create Package"
                : "Save Package"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseFeatureGroupUpsertDialog
